import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { cloneDeep } from 'lodash';
import {
  ConvertUnitsApi,
  SI_UNITS,
  Unit,
  UnitSettings,
  UnitsService,
  UnitsV2,
} from '@schenck/rotec-components';
import { UNITS_MAPPERS } from 'src/utils/constants/units.const';
import { RotorTypeFormsData } from 'src/utils/interfaces/rotor-type-forms-data.interfaces';
import {
  RotorTypeConfigurationModel,
  WeightSetItem,
} from 'src/utils/interfaces/rotor-type.interfaces';
import { UnitData } from 'src/utils/interfaces/units.interfaces';
import { getToleranceConfiguration } from './steps/tolerances-step/tolerances-step-const';
import { EXCLUDED_CONTROLS, FORM_STEPS_FOR_UPDATE } from '../../utils/constants/rotor-type.const';
import { Observable } from 'rxjs/internal/Observable';
import { map, of } from 'rxjs';

export const getSelectedConfiguration = (
  configurations: RotorTypeConfigurationModel[],
  formData: RotorTypeFormsData
): RotorTypeConfigurationModel => {
  const selectedCorrectionPlane = formData.supportSettingsStep.selectedCorrectionPlane;
  const { r1, r2 } = formData.rotorTypeSettingsStep;
  const selectedCofiguration = configurations[selectedCorrectionPlane - 1];

  if (!selectedCofiguration) {
    return null;
  }

  if (!selectedCofiguration.imageOptions) {
    return selectedCofiguration;
  }

  const { imageOptions } = selectedCofiguration;

  if (r1 && r2 && +r1.value === +r2.value) {
    selectedCofiguration.imageUrl = imageOptions[0].url;
    selectedCofiguration.class = imageOptions[0].class || selectedCofiguration.class;
  }

  if (r1 && r2 && +r1.value < +r2.value) {
    selectedCofiguration.imageUrl = imageOptions[1].url;
    selectedCofiguration.class = imageOptions[1].class || selectedCofiguration.class;
  }

  if (r1 && r2 && +r1.value > +r2.value) {
    selectedCofiguration.imageUrl = imageOptions[2].url;
    selectedCofiguration.class = imageOptions[2].class || selectedCofiguration.class;
  }

  return selectedCofiguration;
};

export function requiredValidator(): ValidatorFn {
  return (control: AbstractControl) => (control.value.value === null ? { error: true } : null);
}

export function valueRangeValidator({ min, max }: { min: number; max: number }): ValidatorFn {
  return (control: AbstractControl) =>
    typeof control.value === 'number' && control.value >= min && control.value <= max
      ? null
      : { valueRangeError: { message: 'The value must be between MIN and MAX', min, max } };
}

export function negativeZeroValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    // Check if the value is -0
    if (Object.is(value, -0)) {
      return { negativeZero: true };
    }
    return null;
  };
}

export function greaterZeroValidator(control: AbstractControl): ValidationErrors | null {
  return control.value > 0 ? null : { greaterZeroError: 'The value must be greater than zero' };
}

export const setPreferredUnits = (settings: UnitSettings, unit: Unit, units: UnitsV2[]): string => {
  const settingKey = units.find(({ units }) => units.map(({ name }) => name).includes(unit));
  const unitValue = settingKey ? UNITS_MAPPERS[settingKey.group] : null;

  if (unitValue === 'densityValue' && settings) {
    settings = { ...settings, densityValue: SI_UNITS.density };
  }

  return settings && unitValue ? settings[unitValue] : unit;
};

export const updateForm = (
  formsData: RotorTypeFormsData,
  unitData: UnitData,
  units: UnitsV2[],
  applySettings?: boolean
): RotorTypeFormsData => {
  const fields = cloneDeep(formsData);

  for (const key in fields) {
    FORM_STEPS_FOR_UPDATE.filter(({ name }) => name === key).forEach(({ prefix }) => {
      if (prefix) {
        if (key === 'correctionLocationsStep') {
          const locationType =
            formsData.correctionLocationsStep[prefix].locationType !== 'Free' &&
            formsData.correctionLocationsStep[prefix].locationType === 'Sector'
              ? 'sectors'
              : 'locations';

          for (const locationIdx in fields[key][prefix][locationType]) {
            fields[key][prefix][locationType][locationIdx] = updateControls(
              unitData,
              units,
              fields[key][prefix][locationType][locationIdx],
              applySettings
            );
          }
        }
        fields[key][prefix] = updateControls(unitData, units, fields[key][prefix], applySettings);
      } else {
        fields[key] = updateControls(unitData, units, fields[key], applySettings);
      }
    });
  }

  return fields;
};

export const updateControls = (
  unitData: UnitData,
  units: UnitsV2[],
  controls: any = {},
  applySettings?: boolean
) => {
  const updatedControls = cloneDeep(controls);

  for (const key in updatedControls) {
    const item = updatedControls[key];

    if (item?.subname && unitData.units[item?.name]) {
      item.subname = unitData.units[item.name];
    }

    if (item?.value && unitData.units[item?.name]) {
      item.value = unitData.units[item.name];
    }

    if (typeof item?.value !== 'number' && item?.unit && applySettings) {
      item.unit = EXCLUDED_CONTROLS.includes(key)
        ? item.unit
        : setPreferredUnits(unitData.settings, item.unit, units);
    }
  }

  return updatedControls;
};

export const calculateRollerBedDistance = (formData: RotorTypeFormsData): number => {
  const { a, b, c } = formData.rotorTypeSettingsStep;
  const pedestalPosition2 = +formData.rotorTypeSettingsStep.pedestalPosition2.value;
  const abcGeometryNotFilled = [a, b, c].some((item) => !item?.value && item?.value !== 0);
  const laserLinesActive = formData.supportSettingsStep.laserLinesActive;
  const key = getToleranceConfiguration(
    'iso-calculation',
    laserLinesActive ? 'Laser' : 'NoLaser'
  ).find(({ value }) => formData.supportSettingsStep.selectedCorrectionPlane === value).title;

  return abcGeometryNotFilled
    ? null
    : calculateRollerBedDistanceActions(key, +a.value, +b.value, +c.value, pedestalPosition2);
};

export const calculateRollerBedDistanceActions = (
  key: string,
  a: number,
  b: number,
  c: number,
  pedestalPosition2: number
): number => {
  const calculate: { [key: string]: () => number } = {
    centered: () => a + b + c,
    overhungRight: () => a + b - c,
    centerRight: () => a + b - c,
    polar: () => b - a - c,
    centerLeft: () => b + c - a,
    overhungLeft: () => b + c - a,
    static: () => pedestalPosition2,
  };
  return calculate[key]();
};

export const globalSettings = (): boolean => JSON.parse(localStorage.getItem('globalSettings'));

export const getMaxDecimalPlaces = (numbers: number[]): number => {
  let maxDecimalPlaces = 0;

  numbers.forEach((number) => {
    const numberStr = number.toString();
    const decimalPart = numberStr.split('.')[1];
    if (decimalPart) {
      maxDecimalPlaces = Math.max(maxDecimalPlaces, decimalPart.length);
    }
  });

  return maxDecimalPlaces;
};

/**
 * Returns true If an increment can be computed from the given weights
 * @param sortedWeights The weights sorted by their siValue.
 * @returns true, if an increment can be displayed, false otherwise.
 */
export const canShowIncrement = (sortedWeights: WeightSetItem[]): boolean => {
  // Compute the increment based on sorted weights
  const increment = sortedWeights[0].mass.value;
  // JavaScript, numbers are represented using the IEEE 754 double-precision floating-point format.
  // This format can introduce small rounding errors because not
  // all decimal numbers can be represented exactly in binary.
  const roundToMaxDecimals = (value: number, maxDecimalPlaces) =>
    parseFloat(value.toFixed(maxDecimalPlaces));

  for (let i = 0; i < sortedWeights.length - 1; i++) {
    const current = sortedWeights[i];
    const next = sortedWeights[i + 1];
    const maxDecimalPlaces = getMaxDecimalPlaces([
      next.mass.value.siValue,
      current.mass.value.siValue,
      increment.siValue,
    ]);
    if (
      roundToMaxDecimals(next.mass.value.siValue - current.mass.value.siValue, maxDecimalPlaces) !==
      roundToMaxDecimals(increment.siValue, maxDecimalPlaces)
    ) {
      return false;
    }
  }

  return true;
};

export function radiusValidator(unitsService: UnitsService, convertUnitsApi: ConvertUnitsApi) {
  return (control: AbstractControl): Observable<ValidationErrors | null> => {
    // Radius must be 0.1mm - 10000mm which corresponds to 0,0001m - 10m in SI unit
    // Since we don't know which unit the user has selected, we convert the value to the SI unit first and compare it to the limits

    const MIN_RADIUS_M = 0.0001;
    const MAX_RADIUS_M = 10;

    const unit = control.value.unit;
    const value = control.value.value;
    const siUnit = unitsService.getSItUnitFromUnitType('Length');

    if (value) {
      const convertedValues$ = convertUnitsApi.convertUnits$({
        group: `EPhysUnit_Length`,
        decimalDigits: 10,
        originUnit: unitsService.getUnitIndex(unit, 'Length'),
        targetUnit: unitsService.getUnitIndex(siUnit, 'Length'),
        values: [
          {
            key: 'radius',
            value,
          },
        ],
      });

      return convertedValues$.pipe(
        map((values) => {
          if (values[0].convertedValue < MIN_RADIUS_M || values[0].convertedValue > MAX_RADIUS_M) {
            return { radiusError: 'Radius must be between 0.1mm and 10000mm' };
          }
          return null;
        })
      );
    } else {
      return of(null);
    }
  };
}
